'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { ProductLogoMediumCertifications } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { MemberCertification } from '@/mocks/db/types';
import { MY_PORTAL_PERSON_ID } from '../-shared/-api';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { type LedgerFilterTab, LedgerFilterTabs } from '../-shared/-components/LedgerFilterTabs';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { PersonCell } from '../-shared/-components/ResourceCells';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useMemberCertifications, usePortalPersons } from '../-shared/-query';
import { formatDate, toMapById } from '../-shared/-utils';
const CERTIFICATIONS_PATH = '/individual/portal/lookup/certifications';
const LOOKUP_PATH = '/individual/portal/lookup';
const certificationsSearchSchema = z.object({
  scope: z.enum(['self', 'all']).catch('self'),
  id: z.string().optional()
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/certifications/')({
  validateSearch: certificationsSearchSchema,
  component: CertificationsLookupPage
});
function CertificationsLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: certifications,
    isPending
  } = useMemberCertifications();
  const {
    data: persons
  } = usePortalPersons();
  const personsById = useMemo(() => toMapById(persons), [persons]);
  const selected = id != null ? certifications?.find(c => c.id === id) ?? null : null;
  const rows = useMemo(() => {
    const all = certifications ?? [];
    return scope === 'self' ? all.filter(c => c.holderPersonId === MY_PORTAL_PERSON_ID) : all;
  }, [certifications, scope]);
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('certifications.title'),
        link: {
          to: CERTIFICATIONS_PATH,
          search: {
            scope
          }
        }
      }, {
        label: selected.name
      }]} Logo={ProductLogoMediumCertifications} title={selected.name} subtitle={selected.number} />
        <LedgerContent>
          <ResourceDetailCard title={t('certifications.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('certifications.detail.holder')} value={<PersonCell person={personsById.get(selected.holderPersonId)} />} />
              <Field label={t('certifications.detail.number')} value={selected.number} />
              <Field label={t('certifications.detail.issuer')} value={selected.issuer} />
              <Field label={t('certifications.detail.lastUpdated')} value={formatDate(selected.lastUpdated)} />
              <Field label={t('certifications.detail.expireAt')} value={formatDate(selected.expireAt)} />
              <Field label={t('certifications.detail.renewBy')} value={formatDate(selected.renewBy)} />
              <Field label={t('certifications.detail.note')} value={selected.note === '' ? '—' : selected.note} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<MemberCertification>({
    id: 'name',
    header: t('certifications.headers.name'),
    cell: c => <Text variant="label">{c.name}</Text>
  }), ledgerColumn<MemberCertification>({
    id: 'holder',
    header: t('certifications.headers.holder'),
    cell: c => <PersonCell person={personsById.get(c.holderPersonId)} />
  }), ledgerColumn<MemberCertification>({
    id: 'number',
    header: t('certifications.headers.number'),
    cell: c => c.number
  }), ledgerColumn<MemberCertification>({
    id: 'lastUpdated',
    header: t('certifications.headers.lastUpdated'),
    cell: c => formatDate(c.lastUpdated)
  }), ledgerColumn<MemberCertification>({
    id: 'expireAt',
    header: t('certifications.headers.expireAt'),
    cell: c => formatDate(c.expireAt)
  }), ledgerColumn<MemberCertification>({
    id: 'renewBy',
    header: t('certifications.headers.renewBy'),
    cell: c => formatDate(c.renewBy)
  })];
  const scopeTabs: LedgerFilterTab<'self' | 'all'>[] = [{
    value: 'self',
    label: t('certifications.tabs.self')
  }, {
    value: 'all',
    label: t('certifications.tabs.all')
  }];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('certifications.title')
    }]} Logo={ProductLogoMediumCertifications} title={t('certifications.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<MemberCertification> fillHeight columns={columns} data={rows} isPending={isPending} filterTabs={<LedgerFilterTabs tabs={scopeTabs} value={scope} onSelect={s => void navigate({
        search: prev => ({
          ...prev,
          scope: s
        })
      })} ariaLabel={t('common.filterTabsLabel')} />} getRowId={c => c.id} getSearchText={c => `${c.name} ${c.number} ${personsById.get(c.holderPersonId)?.name ?? ''}`} searchPlaceholder={t('common.search')} onRowSelect={c => void navigate({
        search: prev => ({
          ...prev,
          id: c.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}